/**
 * Visit start helpers (Phase 9B). PA-14: the start sheet lists only PAs the server will accept
 * (validatePaForVisit in the backend): APPROVED, today within the approved dates, units left,
 * and a service category matching the visit. The server re-checks and never blocks care (VIS-06).
 */
import type { Authorization } from '../dtos/patientmr/authorizations.dto';
import { classifyVisitService, NursingServiceType } from './evvRules';
import { paCategoryService, serviceLabel } from '../constants/disciplines';

const UNIT_LABEL: Record<string, string> = { units: 'units', hours: 'hours', visits: 'visits' };

type Pa = Authorization & {
  approvedStartDate?: string | null;
  approvedEndDate?: string | null;
  approvedQuantity?: number | null;
  usedQuantity?: number | null;
  serviceCategory?: string | null;
  payerName?: string | null;
  paNumber?: string;
  unitType?: string | null;
};

const day = (v: string | null | undefined) => String(v || '').slice(0, 10);
export const paStart = (pa: Pa) => day(pa.approvedStartDate || pa.startDate);
export const paEnd = (pa: Pa) => day(pa.approvedEndDate || pa.expirationDate);
export const paUnitsLeft = (pa: Pa) => Math.max(0, (Number(pa.approvedQuantity) || 0) - (Number(pa.usedQuantity) || 0));

/** True when the PA's service category covers this visit (RN/LVN PDN ↔ pdn, PT ↔ pt, …). */
export function paMatchesVisit(pa: Pa, discipline: string, serviceType?: NursingServiceType | '' | null): boolean {
  const category = String(pa.serviceCategory || '').toLowerCase();
  const paService = paCategoryService(category);
  if (!paService) return String(pa.discipline || '').toUpperCase() === String(discipline || '').toUpperCase();
  return classifyVisitService({ discipline, serviceType, paServiceCategory: category }).service === paService;
}

/** PA-14: the PAs a visit starting `today` can link to, soonest-ending first. */
export function eligibleVisitPas(pas: Pa[], { today, discipline, serviceType }: { today: string; discipline: string; serviceType?: NursingServiceType | '' | null }): Pa[] {
  return (pas || [])
    .filter((pa) => String(pa.status || '').toUpperCase() === 'APPROVED')
    .filter((pa) => paStart(pa) && paEnd(pa) && paStart(pa) <= today && today <= paEnd(pa))
    .filter((pa) => paUnitsLeft(pa) > 0)
    .filter((pa) => paMatchesVisit(pa, discipline, serviceType))
    .sort((a, b) => paEnd(a).localeCompare(paEnd(b)));
}

/** `[PA-ID] · Service · Payer · N units left` (PA-14). */
export function paOptionLabel(pa: Pa): string {
  const id = pa.paNumber || pa.authNumber || String(pa.id || '').slice(0, 8);
  const categoryService = paCategoryService(pa.serviceCategory);
  const service = (categoryService && serviceLabel(categoryService)) || pa.discipline || 'Service';
  const payer = pa.payerName || 'Payer not set';
  const unit = UNIT_LABEL[String(pa.unitType || 'visits').toLowerCase()] || 'units';
  return `[${id}] · ${service} · ${payer} · ${paUnitsLeft(pa)} ${unit} left`;
}

/** The location payload for a visit start/end: coordinates, or explicitly unavailable (VIS-04). */
export function visitLocationPayload(coords: { latitude: number; longitude: number } | null) {
  return coords && !(coords.latitude === 0 && coords.longitude === 0)
    ? { location: { latitude: coords.latitude, longitude: coords.longitude }, locationUnavailable: false }
    : { location: null, locationUnavailable: true };
}

/** One GPS fix (5 s). Resolves null when denied, unsupported or slow — never throws. */
export function getCurrentCoords(timeoutMs = 5000): Promise<{ latitude: number; longitude: number } | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: { latitude: number; longitude: number } | null) => { if (!done) { done = true; resolve(v); } };
    const timer = setTimeout(() => finish(null), timeoutMs + 500);
    navigator.geolocation.getCurrentPosition(
      (p) => { clearTimeout(timer); finish({ latitude: p.coords.latitude, longitude: p.coords.longitude }); },
      () => { clearTimeout(timer); finish(null); },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60000 },
    );
  });
}
